import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import TransitionLink from './TransitionLink';
import { useMobileDetection } from '../hooks/useMobileDetection';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const LightHeader = ({ ctaLabel }) => {
    const [isOverWhiteSection, setIsOverWhiteSection] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { isMobile } = useMobileDetection();
    const { language } = useLanguage();
    const t = useTranslation(language);
    const location = useLocation();

    useEffect(() => {
        const headerHeight = 80;

        const handleScroll = () => {
            const whiteSections = document.querySelectorAll('.bg-white');
            let overWhite = false;

            whiteSections.forEach((section) => {
                const rect = section.getBoundingClientRect();
                if (rect.top <= headerHeight && rect.bottom >= 0) {
                    overWhite = true;
                }
            });

            setIsOverWhiteSection(overWhite);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu on route change
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Lock body scroll when menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
            if (window.lenis) window.lenis.stop();
        } else {
            document.body.style.overflow = '';
            if (window.lenis) window.lenis.start();
        }
        return () => {
            document.body.style.overflow = '';
            if (window.lenis) window.lenis.start();
        };
    }, [menuOpen]);

    const textColorClass = menuOpen ? 'text-white' : (isOverWhiteSection ? 'text-black' : 'text-white');
    const hamburgerColor = menuOpen ? 'white' : (isOverWhiteSection ? 'black' : 'white');

    const allLinks = [
        { to: '/', label: t('home') },
        { to: '/works', label: t('works') },
        { to: '/code', label: t('code') },
        { to: '/about', label: t('about') },
    ];

    const MobileNav = () => (
        <div className="flex items-center gap-3">
            <LanguageSwitcher textColorClass={textColorClass} />
            <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Toggle menu"
                className="relative z-[60] w-9 h-9"
            >
                <span style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: '20px',
                    height: '1.5px',
                    background: hamburgerColor,
                    borderRadius: '1px',
                    transformOrigin: 'center',
                    transition: 'transform 0.45s cubic-bezier(0.76,0,0.24,1)',
                    transform: menuOpen
                        ? 'translate(-50%, -50%) rotate(45deg)'
                        : 'translate(-50%, calc(-50% - 6px))',
                }} />
                <span style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: '20px',
                    height: '1.5px',
                    background: hamburgerColor,
                    borderRadius: '1px',
                    transformOrigin: 'center',
                    transition: 'opacity 0.2s ease 0.05s, transform 0.45s cubic-bezier(0.76,0,0.24,1)',
                    transform: 'translate(-50%, -50%)',
                    opacity: menuOpen ? 0 : 1,
                }} />
                <span style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: '20px',
                    height: '1.5px',
                    background: hamburgerColor,
                    borderRadius: '1px',
                    transformOrigin: 'center',
                    transition: 'transform 0.45s cubic-bezier(0.76,0,0.24,1)',
                    transform: menuOpen
                        ? 'translate(-50%, -50%) rotate(-45deg)'
                        : 'translate(-50%, calc(-50% + 6px))',
                }} />
            </button>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 bg-black flex flex-col justify-between px-8 py-24"
                    >
                        <nav className="flex flex-col gap-2">
                            {allLinks.map((link, i) => (
                                <motion.div
                                    key={link.to}
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ delay: i * 0.06, duration: 0.3 }}
                                >
                                    <TransitionLink
                                        to={link.to}
                                        onClick={() => setMenuOpen(false)}
                                        className={`block text-5xl font-black tracking-tight text-white hover:text-accent transition-colors py-2 ${location.pathname === link.to ? 'text-accent' : ''}`}
                                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {link.label}
                                    </TransitionLink>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ delay: allLinks.length * 0.06, duration: 0.3 }}
                            >
                                <a
                                    href="mailto:info@paoloallegretti.com"
                                    onClick={() => setMenuOpen(false)}
                                    className="inline-block mt-4 text-white text-lg font-medium border border-white/30 px-6 py-3 rounded-full hover:border-orange-400 hover:text-orange-400 transition-colors duration-300"
                                >
                                    {ctaLabel}
                                </a>
                            </motion.div>
                        </nav>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="flex items-center justify-end"
                        >
                            <span className="text-white/30 text-xs font-mono tracking-widest uppercase">Paolo Allegretti</span>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    return (
        <header className="fixed top-0 left-0 w-full z-50 py-5 px-8 md:px-16 flex items-center justify-between min-h-[36px]">
            <TransitionLink to="/" className={`${textColorClass} text-xl font-black tracking-tight hover:opacity-70 transition-opacity transition-colors duration-300 leading-none flex items-center h-9`}>
                Paolo <span className="text-orange-400">A.</span>
            </TransitionLink>
            {isMobile ? (
                <MobileNav />
            ) : (
                <div className="flex items-center gap-4">
                    <LanguageSwitcher textColorClass={textColorClass} />
                    <a
                        href="mailto:info@paoloallegretti.com"
                        className={`${textColorClass} text-sm font-medium border ${isOverWhiteSection ? 'border-black/30' : 'border-white/30'} px-4 py-2 rounded-full hover:border-orange-400 hover:text-orange-400 transition-colors duration-300`}
                    >
                        {ctaLabel}
                    </a>
                </div>
            )}
        </header>
    );
};

export default LightHeader;
